import {
  differenceInCalendarMonths,
  endOfMonth,
  format,
  isValid,
  parse,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns';
import {
  FidcComparatorPoint,
  FidcComparatorResponse,
  FidcComparatorSeries,
} from 'src/services/fidcComparator';

const BASE_MONTH_FORMAT = 'yyyy-MM';
const ISO_DATE_FORMAT = 'yyyy-MM-dd';
const GRAPH_PERIOD_PATTERN = /^(\d+)m?$/i;

const FIRST_MONTH = 1;
const LAST_MONTH = 12;
const PREVIOUS_MONTH_OFFSET = 1;
const DECIMAL_RADIX = 10;
const DEFAULT_BASE_MONTH_OPTIONS_COUNT = 24;

const BASE_MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export type BaseMonthOption = {
  value: string;
  label: string;
};

type GraphPeriodRange = {
  from: string;
  to: string;
  months: number;
};

type DateRange = Pick<GraphPeriodRange, 'from' | 'to'>;

export const formatYearMonth = (date: Date) => format(date, BASE_MONTH_FORMAT);

const formatIsoDate = (date: Date) => format(date, ISO_DATE_FORMAT);

export const formatChartDateLabel = (date: string) => {
  const baseMonth = date.slice(0, BASE_MONTH_FORMAT.length);

  if (!isValidBaseMonth(baseMonth)) return date;

  return `${baseMonth.slice(5, 7)}-${baseMonth.slice(0, 4)}`;
};

export const getDefaultBaseMonth = (date = new Date()) =>
  formatYearMonth(startOfMonth(subMonths(date, PREVIOUS_MONTH_OFFSET)));

export const isValidBaseMonth = (value: string | null): value is string => {
  if (!value) return false;

  const parsedDate = parse(value, BASE_MONTH_FORMAT, new Date());
  const month = Number.parseInt(value.slice(5, 7), DECIMAL_RADIX);

  return (
    isValid(parsedDate) &&
    formatYearMonth(parsedDate) === value &&
    month >= FIRST_MONTH &&
    month <= LAST_MONTH
  );
};

export const resolveBaseMonth = (value: string | null, date = new Date()) =>
  isValidBaseMonth(value) ? value : getDefaultBaseMonth(date);

export const formatBaseMonthLabel = (baseMonth: string) => {
  if (!isValidBaseMonth(baseMonth)) return baseMonth;

  const monthIndex = Number.parseInt(baseMonth.slice(5, 7), DECIMAL_RADIX) - 1;
  return `${BASE_MONTH_LABELS[monthIndex]}/${baseMonth.slice(0, 4)}`;
};

const pointToBaseMonth = (point: FidcComparatorPoint) => {
  const baseMonth = point.date.slice(0, BASE_MONTH_FORMAT.length);
  return isValidBaseMonth(baseMonth) ? baseMonth : null;
};

const addSeriesBaseMonths = (
  options: Set<string>,
  series: FidcComparatorSeries[] | null | undefined,
) => {
  (series ?? []).forEach((item) => {
    item.data.forEach((point) => {
      const baseMonth = pointToBaseMonth(point);
      if (baseMonth) options.add(baseMonth);
    });
  });
};

const addPointBaseMonths = (
  options: Set<string>,
  points: FidcComparatorPoint[] | null | undefined,
) => {
  (points ?? []).forEach((point) => {
    const baseMonth = pointToBaseMonth(point);
    if (baseMonth) options.add(baseMonth);
  });
};

const buildDefaultBaseMonthValues = (date = new Date()) =>
  Array.from({ length: DEFAULT_BASE_MONTH_OPTIONS_COUNT }, (_, index) =>
    formatYearMonth(
      startOfMonth(subMonths(date, index + PREVIOUS_MONTH_OFFSET)),
    ),
  );

const mapBaseMonthValuesToOptions = (
  values: Iterable<string>,
): BaseMonthOption[] =>
  Array.from(new Set(values))
    .sort((first, second) => second.localeCompare(first))
    .map((value) => ({ value, label: formatBaseMonthLabel(value) }));

export const mergeBaseMonthOptions = (
  ...optionGroups: BaseMonthOption[][]
): BaseMonthOption[] =>
  mapBaseMonthValuesToOptions(
    optionGroups.flatMap((options) => options.map((option) => option.value)),
  );

export const buildBaseMonthOptions = ({
  data,
  selectedBaseMonth,
  date = new Date(),
}: {
  data?: FidcComparatorResponse;
  selectedBaseMonth: string;
  date?: Date;
}): BaseMonthOption[] => {
  const baseMonths = new Set<string>();

  if (data) {
    const { series } = data;
    addSeriesBaseMonths(baseMonths, series.monthlyReturns);
    addSeriesBaseMonths(baseMonths, series.base100);
    addSeriesBaseMonths(baseMonths, series.pl);
    addSeriesBaseMonths(baseMonths, series.pdd);
    addSeriesBaseMonths(baseMonths, series.atraso);
    addSeriesBaseMonths(baseMonths, series.atrasoOverPl);
    addSeriesBaseMonths(baseMonths, series.atrasoOverPdd);
    addSeriesBaseMonths(baseMonths, series.pddOverAtraso);
    addSeriesBaseMonths(baseMonths, series.pddOverPl);
    addSeriesBaseMonths(baseMonths, series.creditRightsOverPl);
    addSeriesBaseMonths(baseMonths, series.subordination);
    addPointBaseMonths(baseMonths, series.cdiBase100);
    addPointBaseMonths(baseMonths, series.cdiMonthly);

    [
      data.period.from,
      data.period.to,
      data.graphPeriod.from,
      data.graphPeriod.to,
    ]
      .map((value) => value.slice(0, BASE_MONTH_FORMAT.length))
      .filter(isValidBaseMonth)
      .forEach((value) => baseMonths.add(value));
  }

  if (baseMonths.size === 0) {
    buildDefaultBaseMonthValues(date).forEach((value) => baseMonths.add(value));
  }

  if (isValidBaseMonth(selectedBaseMonth)) {
    baseMonths.add(selectedBaseMonth);
  }

  return mapBaseMonthValuesToOptions(baseMonths);
};

const parseBaseMonth = (baseMonth: string): Date => {
  if (!isValidBaseMonth(baseMonth)) {
    throw new Error(`baseMonth deve estar no formato ${BASE_MONTH_FORMAT}.`);
  }

  return startOfMonth(parse(baseMonth, BASE_MONTH_FORMAT, new Date()));
};

const parseGraphPeriodInMonths = (graphPeriod: string): number => {
  const match = graphPeriod.trim().match(GRAPH_PERIOD_PATTERN);

  if (!match) {
    throw new Error(
      'graphPeriod deve ser um número de meses, exemplo: "3" ou "3m".',
    );
  }

  const months = Number.parseInt(match[1], DECIMAL_RADIX);

  if (!Number.isSafeInteger(months) || months < FIRST_MONTH) {
    throw new Error(`graphPeriod deve ser maior ou igual a ${FIRST_MONTH}.`);
  }

  return months;
};

export const buildGraphPeriodRange = (
  baseMonth: string,
  graphPeriod: string,
): GraphPeriodRange => {
  const baseMonthDate = parseBaseMonth(baseMonth);
  const months = parseGraphPeriodInMonths(graphPeriod);

  const from = startOfMonth(subMonths(baseMonthDate, months));

  const to = endOfMonth(baseMonthDate);

  return {
    from: formatIsoDate(from),
    to: formatIsoDate(to),
    months,
  };
};

const filterPointsByRange = (
  points: FidcComparatorPoint[] | null | undefined,
  range: DateRange,
) =>
  (points ?? []).filter((point) => {
    const date = point.date.slice(0, ISO_DATE_FORMAT.length);

    return date >= range.from && date <= range.to;
  });

const filterSeriesByRange = (
  series: FidcComparatorSeries[] | null | undefined,
  range: DateRange,
) =>
  (series ?? []).map((item) => ({
    ...item,
    data: filterPointsByRange(item.data, range),
  }));

const buildRangeFromSeries = (
  series: FidcComparatorSeries[] | null | undefined,
): GraphPeriodRange | null => {
  const safeSeries = series ?? [];

  const bounds = safeSeries
    .map((item) => item.data.filter((point) => point.value !== null))
    .filter((points) => points.length > 0)
    .map((points) => {
      const sortedPoints = [...points].sort((first, second) =>
        first.date.localeCompare(second.date),
      );
      const firstPointDate = parseISO(sortedPoints[0].date);
      const lastPoint =
        sortedPoints[sortedPoints.length - 1] ?? sortedPoints[0];
      const lastPointDate = parseISO(lastPoint.date);

      return {
        from: formatIsoDate(startOfMonth(firstPointDate)),
        to: formatIsoDate(endOfMonth(lastPointDate)),
      };
    });

  if (!bounds.length || bounds.length !== safeSeries.length) return null;

  const from = bounds.reduce(
    (latestStart, current) =>
      current.from > latestStart ? current.from : latestStart,
    bounds[0].from,
  );
  const to = bounds.reduce(
    (earliestEnd, current) =>
      current.to < earliestEnd ? current.to : earliestEnd,
    bounds[0].to,
  );

  if (from > to) return null;

  return {
    from,
    to,
    months: Math.max(
      differenceInCalendarMonths(parseISO(to), parseISO(from)),
      0,
    ),
  };
};

const clearSeries = (
  series: ReadonlyArray<FidcComparatorSeries> | null | undefined,
) =>
  (series ?? []).map((item) => ({
    ...item,
    data: [],
  }));

export const trimFidcComparatorChartsToCommonHistory = (
  data: FidcComparatorResponse,
): FidcComparatorResponse => {
  const commonRange = buildRangeFromSeries(data.series.monthlyReturns);

  if (!commonRange) {
    return {
      ...data,
      series: {
        ...data.series,
        monthlyReturns: clearSeries(data.series.monthlyReturns),
        base100: clearSeries(data.series.base100),
        cdiBase100: [],
        cdiMonthly: [],
      },
    };
  }

  const filteredData = filterFidcComparatorChartsData(data, commonRange);

  return {
    ...filteredData,
    series: {
      ...filteredData.series,
      pl: data.series.pl,
      pdd: data.series.pdd,
      atraso: data.series.atraso,
      atrasoOverPl: data.series.atrasoOverPl,
      atrasoOverPdd: data.series.atrasoOverPdd,
      pddOverAtraso: data.series.pddOverAtraso,
      pddOverPl: data.series.pddOverPl,
      creditRightsOverPl: data.series.creditRightsOverPl,
      subordination: data.series.subordination,
    },
  };
};

export const filterFidcComparatorChartsData = (
  data: FidcComparatorResponse,
  range: GraphPeriodRange,
): FidcComparatorResponse => ({
  ...data,
  graphPeriod: range,
  series: {
    ...data.series,
    monthlyReturns: filterSeriesByRange(data.series.monthlyReturns, range),
    base100: filterSeriesByRange(data.series.base100, range),
    cdiBase100: filterPointsByRange(data.series.cdiBase100, range),
    cdiMonthly: filterPointsByRange(data.series.cdiMonthly, range),
    pl: filterSeriesByRange(data.series.pl, range),
    pdd: filterSeriesByRange(data.series.pdd, range),
    atraso: filterSeriesByRange(data.series.atraso ?? [], range),
    atrasoOverPl: filterSeriesByRange(data.series.atrasoOverPl ?? [], range),
    atrasoOverPdd: filterSeriesByRange(data.series.atrasoOverPdd ?? [], range),
    pddOverAtraso: filterSeriesByRange(data.series.pddOverAtraso ?? [], range),
    pddOverPl: filterSeriesByRange(data.series.pddOverPl, range),
    creditRightsOverPl: filterSeriesByRange(
      data.series.creditRightsOverPl ?? [],
      range,
    ),
    subordination: filterSeriesByRange(data.series.subordination, range),
  },
});
